import { useState } from 'react'
import ModalComponent from '..'
import {
  AdminModule,
  QueryPropsLogs,
} from '../../../redux-toolkits/audit-logs/auditLogs.types'
import {
  useGetRolesQuery,
  useGetTeamMembersQuery,
} from '../../../redux-toolkits/user/user.slice'
import { Accordion } from '../../accordion'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import Spinner from '../../spinner/Spinner'

const formatModuleName = (m: string) =>
  m
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())

export const FilterAuditLogsModal = ({
  modalOpen,
  handleModalClose,
  setModalOpen,
  query,
  onSucess,
}: {
  modalOpen: boolean
  handleModalClose: () => void
  setModalOpen: (open: boolean) => void
  query?: QueryPropsLogs
  onSucess: (query: QueryPropsLogs) => void
}) => {
  const [adminSearch, setAdminSearch] = useState<string | undefined>(undefined)

  const { data: userData, isFetching: fetchingAdmins } = useGetTeamMembersQuery(
    { skip: 0, limit: 1000, search: adminSearch },
  )

  const { data: rolesData, isFetching: fetchingRoles } = useGetRolesQuery({
    skip: 0,
    limit: 1000,
  })

  const teamMembers = userData?.data?.admins.filter((m) => !m.deleted) ?? []
  const roles = rolesData?.data ?? []

  return (
    <ModalComponent
      open={modalOpen}
      setOpen={setModalOpen}
      onCloseCallback={() => {
        handleModalClose()
        setAdminSearch(undefined)
      }}
      title={undefined}
      position="right"
    >
      <Button
        label="Reset filters"
        onClick={() => {
          onSucess({
            ...query,
            skip: 0,
            module: undefined,
            adminId: undefined,
            adminRole: undefined,
          })
        }}
        primary={false}
        variant="plain"
        className="!px-0 !py-0 mb-10"
      />

      <Accordion title="Module" className="pb-4 border-b" open={true}>
        <div className="flex flex-col gap-2 w-full max-h-[250px] overflow-y-scroll">
          <Checkbox
            checkStatus={!query?.module}
            disabled={false}
            handleChange={() => {
              onSucess({ ...query, skip: 0, module: undefined })
            }}
            text="All"
            textPosition="left"
            className="w-full !justify-between !font-normal"
          />
          {Object.keys(AdminModule).map((k) => {
            const m = AdminModule[k as keyof typeof AdminModule]

            return (
              <Checkbox
                key={m}
                checkStatus={query?.module === m}
                disabled={false}
                handleChange={() => {
                  onSucess({
                    ...query,
                    skip: 0,
                    module: m,
                  })
                }}
                text={formatModuleName(k)}
                textPosition="left"
                className="w-full !justify-between !font-normal"
              />
            )
          })}
        </div>
      </Accordion>

      <Accordion title="Admin User" className="py-4 border-b" open={true}>
        <SearchInput
          placeholder="Search admin users"
          searchFunction={(val) => setAdminSearch(val || undefined)}
          className="!max-w-full mb-3"
        />

        {fetchingAdmins ? (
          <Spinner fullScreen={false} className="py-4" />
        ) : (
          <div className="flex flex-col gap-2 w-full max-h-[250px] overflow-y-scroll">
            <Checkbox
              checkStatus={!query?.adminId}
              disabled={false}
              handleChange={() => {
                onSucess({ ...query, skip: 0, adminId: undefined })
              }}
              text="All"
              textPosition="left"
              className="w-full !justify-between !font-normal"
            />
            {teamMembers.map((member) => (
              <Checkbox
                key={member._id}
                checkStatus={query?.adminId === member._id}
                disabled={false}
                handleChange={() => {
                  onSucess({ ...query, skip: 0, adminId: member._id })
                }}
                text={`${member.firstname} ${member.lastname}`}
                textPosition="left"
                className="w-full !justify-between !font-normal"
              />
            ))}
          </div>
        )}
      </Accordion>

      <Accordion title="Role" className="py-4 border-b-0" open={true}>
        {fetchingRoles ? (
          <Spinner fullScreen={false} className="py-4" />
        ) : (
          <div className="flex flex-col gap-2 w-full max-h-[250px] overflow-y-scroll">
            <Checkbox
              checkStatus={!query?.adminRole}
              disabled={false}
              handleChange={() => {
                onSucess({ ...query, skip: 0, adminRole: undefined })
              }}
              text="All"
              textPosition="left"
              className="w-full !justify-between !font-normal"
            />
            {roles.map((role) => (
              <Checkbox
                key={role._id}
                checkStatus={query?.adminRole === role.name}
                disabled={false}
                handleChange={() => {
                  onSucess({ ...query, skip: 0, adminRole: role.name })
                }}
                text={role.name}
                textPosition="left"
                className="w-full !justify-between !font-normal"
              />
            ))}
          </div>
        )}
      </Accordion>
    </ModalComponent>
  )
}
